import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { ArrowLeft, Calendar as CalendarIcon, Clock, User, Check, X, CalendarClock } from "lucide-react";
import { format } from "date-fns";
import { fromZonedTime } from "date-fns-tz";
import { toast } from "sonner";
import { formatTime } from "@/lib/utils";

interface Booking {
  id: string;
  booking_datetime: string; // UTC ISO timestamp
  booking_timezone?: string;
  duration_minutes: number;
  price: number;
  status: string;
  user_id: string;
  denied_by?: string[];
  video_conference_link?: string;
}

interface Profile {
  full_name: string;
  email: string;
}

interface BlockedTime {
  coach_id: string;
  blocked_date: string;
  start_time: string;
  end_time: string;
}

const CoachBookingAction = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [customerProfile, setCustomerProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [showProposeDialog, setShowProposeDialog] = useState(false);
  const [proposedDate, setProposedDate] = useState<Date | undefined>(new Date());
  const [proposedTime, setProposedTime] = useState<string>("");
  const [blockedTimes, setBlockedTimes] = useState<BlockedTime[]>([]);

  const timeSlots = [
    { value: "09:00", label: "9:00 AM" },
    { value: "10:00", label: "10:00 AM" },
    { value: "11:00", label: "11:00 AM" },
    { value: "12:00", label: "12:00 PM" },
    { value: "13:00", label: "1:00 PM" },
    { value: "14:00", label: "2:00 PM" },
    { value: "15:00", label: "3:00 PM" },
    { value: "16:00", label: "4:00 PM" },
    { value: "17:00", label: "5:00 PM" },
  ];

  useEffect(() => {
    fetchBookingDetails();
  }, [bookingId]);

  useEffect(() => {
    if (proposedDate && showProposeDialog) {
      fetchBlockedTimes();
    }
  }, [proposedDate, showProposeDialog]);

  const fetchBookingDetails = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate("/auth");
        return;
      }

      const { data: bookingData, error: bookingError } = await supabase
        .from("bookings")
        .select("*")
        .eq("id", bookingId)
        .maybeSingle();

      if (bookingError) throw bookingError;
      
      if (!bookingData) {
        setBooking(null);
        setLoading(false);
        return;
      }

      setBooking(bookingData);

      if (bookingData?.user_id) {
        const { data: customerData } = await supabase
          .from("profiles")
          .select("full_name, email")
          .eq("id", bookingData.user_id)
          .single();

        setCustomerProfile(customerData);
      }
    } catch (error) {
      console.error("Error fetching booking:", error);
      toast.error("Failed to load booking details");
    } finally {
      setLoading(false);
    }
  };

  const fetchBlockedTimes = async () => {
    if (!proposedDate) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const dateStr = format(proposedDate, "yyyy-MM-dd");
      const { data, error } = await supabase
        .from("blocked_times")
        .select("*")
        .eq("coach_id", user.id)
        .eq("blocked_date", dateStr);

      if (error) throw error;
      setBlockedTimes(data || []);
    } catch (error) {
      console.error("Error fetching blocked times:", error);
    }
  };

  const isTimeSlotAvailable = (timeSlot: string): boolean => {
    if (!proposedDate) return false;

    // Check if the time is in the past
    const now = new Date();
    const slotDateTime = new Date(proposedDate);
    const [hours, minutes] = timeSlot.split(":").map(Number);
    slotDateTime.setHours(hours, minutes, 0, 0);

    // Add 2 hour buffer for booking
    const twoHoursFromNow = new Date(now.getTime() + 2 * 60 * 60 * 1000);
    if (slotDateTime <= twoHoursFromNow) {
      return false;
    }

    // Check if the time is blocked
    const isBlocked = blockedTimes.some((blockedTime) => {
      const blockStart = blockedTime.start_time;
      const blockEnd = blockedTime.end_time;
      return timeSlot >= blockStart && timeSlot < blockEnd;
    });

    return !isBlocked;
  };

  const sendNotificationEmail = async (status: string, proposedDatetimeUTC?: string) => {
    try {
      // Get coach profile
      const { data: { user } } = await supabase.auth.getUser();
      let coachName = undefined;
      
      if (user) {
        const { data: coachProfile } = await supabase
          .from("profiles")
          .select("full_name")
          .eq("id", user.id)
          .maybeSingle();
        coachName = coachProfile?.full_name || undefined;
      }

      await supabase.functions.invoke('send-booking-notification', {
        body: {
          customerEmail: customerProfile?.email,
          customerName: customerProfile?.full_name || "Customer",
          bookingDatetime: booking!.booking_datetime,
          bookingTimezone: booking!.booking_timezone,
          duration: booking!.duration_minutes,
          status: status,
          proposedDatetime: proposedDatetimeUTC,
          bookingId: booking!.id,
          coachName: coachName,
          videoConferenceLink: booking!.video_conference_link,
        },
      });
    } catch (error) {
      console.error("Error sending email:", error);
    }
  };

  const handleAccept = async () => {
    if (!booking) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("You must be logged in");
        return;
      }

      // Check if booking has already passed
      const bookingDateTime = new Date(booking.booking_datetime);
      const now = new Date();
      
      if (bookingDateTime < now) {
        toast.error("Cannot accept a booking that has already passed");
        navigate("/coach");
        return;
      }

      const roomName = `vocalzing-${booking.id.substring(0, 8)}`;
      const videoLink = `https://meet.jit.si/${roomName}#config.prejoinConfig.enabled=false&config.requireDisplayName=false`;

      // Prevent race conditions: only update if booking is still unassigned
      const { data, error } = await supabase
        .from("bookings")
        .update({ 
          status: "confirmed",
          coach_id: user.id,
          video_conference_link: videoLink
        })
        .eq("id", booking.id)
        .is("coach_id", null)
        .select();

      if (error) throw error;

      // Check if the update actually happened (no rows means another coach already accepted)
      if (!data || data.length === 0) {
        toast.error("This booking has already been accepted by another coach");
        navigate("/coach");
        return;
      }

      // Capture (approve) the payment - this charges the customer
      console.log("Capturing payment for booking:", booking.id);
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/capture-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ bookingId: booking.id }),
      });

      if (!response.ok) {
        const result = await response.json();
        console.error("Payment capture failed:", result);
        throw new Error(result.error || 'Failed to capture payment');
      }

      console.log("Payment captured successfully for booking:", booking.id);
      await sendNotificationEmail("confirmed");
      toast.success("Booking accepted! Payment has been captured.");
      navigate("/coach");
    } catch (error) {
      console.error("Error accepting booking:", error);
      toast.error("Failed to accept booking: " + (error instanceof Error ? error.message : "Unknown error"));
    }
  };

  const handleDeny = async () => {
    if (!booking) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("You must be logged in");
        return;
      }

      // Add this coach to the denied_by array
      const currentDeniedBy = booking.denied_by || [];
      
      // Check if this coach has already denied - if so, don't add again
      if (currentDeniedBy.includes(user.id)) {
        toast.error("You have already denied this booking");
        navigate("/coach");
        return;
      }
      
      const updatedDeniedBy = [...currentDeniedBy, user.id];
      
      // Get all current coach IDs
      const { data: currentCoaches, error: coachError } = await supabase
        .from("user_roles")
        .select("user_id")
        .eq("role", "coach");

      if (coachError) throw coachError;
      
      const currentCoachIds = new Set(currentCoaches?.map(c => c.user_id) || []);
      const totalCoachCount = currentCoachIds.size;
      
      // Count how many CURRENT coaches have denied (filter out old/invalid coach IDs)
      const currentCoachesWhoDenied = updatedDeniedBy.filter(id => currentCoachIds.has(id));
      const uniqueCurrentCoachDenials = new Set(currentCoachesWhoDenied).size;
      
      console.log(`Coach denied booking. Total current coaches: ${totalCoachCount}, Current coaches who denied: ${uniqueCurrentCoachDenials}`);

      // If all CURRENT coaches have denied, update status to denied and cancel payment
      const allCoachesDenied = totalCoachCount > 0 && uniqueCurrentCoachDenials >= totalCoachCount;
      
      const updateData: any = { denied_by: updatedDeniedBy };
      if (allCoachesDenied) {
        updateData.status = "denied";
        console.log("All coaches have denied - updating status to denied");
      }

      const { error } = await supabase
        .from("bookings")
        .update(updateData)
        .eq("id", booking.id);

      if (error) throw error;

      // If all coaches denied, cancel the payment
      if (allCoachesDenied) {
        try {
          console.log("Canceling payment for fully denied booking");
          await supabase.functions.invoke('cancel-payment', {
            body: { bookingId: booking.id }
          });
          toast.success("Booking denied by all coaches - payment has been canceled");
        } catch (cancelError) {
          console.error("Error canceling payment:", cancelError);
          toast.error("Booking denied but payment cancellation failed");
        }
      } else {
        toast.success("Booking denied - other coaches can still accept this booking");
      }
      
      navigate("/coach");
    } catch (error) {
      console.error("Error denying booking:", error);
      toast.error("Failed to deny booking");
    }
  };

  const handleProposeTime = async () => {
    if (!proposedDate || !proposedTime || !booking) {
      toast.error("Please select both date and time");
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("You must be logged in");
        return;
      }

      const timezone = booking?.booking_timezone || "America/New_York";
      const proposedDateStr = format(proposedDate, "yyyy-MM-dd");
      const proposedDatetimeUTC = fromZonedTime(
        new Date(`${proposedDateStr}T${proposedTime}`),
        timezone
      ).toISOString();

      const { error } = await supabase
        .from("bookings")
        .update({ 
          proposed_datetime: proposedDatetimeUTC,
          proposed_by: user.id,
          status: "proposed"
        })
        .eq("id", booking.id);

      if (error) throw error;

      await sendNotificationEmail("proposed", proposedDatetimeUTC);
      toast.success("Alternative time proposed successfully!");
      setShowProposeDialog(false);
      navigate("/coach");
    } catch (error) {
      console.error("Error proposing time:", error);
      toast.error("Failed to propose alternative time");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading booking details...</p>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Booking Not Found</CardTitle>
            <CardDescription>This booking doesn't exist or has already been processed.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/coach")}>Go to Coach Portal</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (booking.status !== "pending") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Booking Already Processed</CardTitle>
            <CardDescription>
              This booking has already been {booking.status}. Check your coach portal for details.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/coach")}>Go to Coach Portal</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-3xl mx-auto space-y-6">
        <Button
          variant="ghost"
          onClick={() => navigate("/coach")}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Coach Portal
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl">Booking Request</CardTitle>
                <CardDescription>Review and respond to this booking request</CardDescription>
              </div>
              <Badge variant="secondary">PENDING</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {customerProfile && (
              <div className="flex items-start gap-3">
                <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">Student</p>
                  <p className="text-sm text-muted-foreground">{customerProfile.full_name}</p>
                  <p className="text-sm text-muted-foreground">{customerProfile.email}</p>
                </div>
              </div>
            )}

            <Separator />

            <div className="flex items-start gap-3">
              <CalendarIcon className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium">Date</p>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(booking.booking_datetime), "EEEE, MMMM d, yyyy")}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium">Time & Duration</p>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(booking.booking_datetime), "h:mm a")} ({booking.duration_minutes} minutes)
                </p>
              </div>
            </div>

            <Separator />

            <div className="flex items-start gap-3">
              <div className="h-5 w-5 text-muted-foreground mt-0.5">$</div>
              <div>
                <p className="font-medium">Price</p>
                <p className="text-sm text-muted-foreground">${booking.price}</p>
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <p className="font-medium">Your Response</p>
              <div className="flex flex-col gap-2">
                <Button
                  onClick={handleAccept}
                  className="w-full"
                  size="lg"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Accept Booking
                </Button>
                <Button
                  onClick={handleDeny}
                  variant="destructive"
                  className="w-full"
                  size="lg"
                >
                  <X className="h-4 w-4 mr-2" />
                  Deny Booking
                </Button>
                <Dialog open={showProposeDialog} onOpenChange={setShowProposeDialog}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full"
                      size="lg"
                    >
                      <CalendarClock className="h-4 w-4 mr-2" />
                      Propose Alternative Time
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Propose Alternative Time</DialogTitle>
                      <DialogDescription>
                        Suggest a different date and time for this booking
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 pt-2">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Date</label>
                        <div className="flex justify-center">
                          <Calendar
                            mode="single"
                            selected={proposedDate}
                            onSelect={setProposedDate}
                            disabled={(date) => date < new Date()}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Time</label>
                        <div className="grid grid-cols-3 gap-2">
                          {timeSlots.map((slot) => {
                            const available = isTimeSlotAvailable(slot.value);
                            return (
                              <Button
                                key={slot.value}
                                variant={proposedTime === slot.value ? "default" : "outline"}
                                size="sm"
                                onClick={() => setProposedTime(slot.value)}
                                disabled={!available}
                              >
                                {slot.label}
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                      <Button 
                        onClick={handleProposeTime}
                        className="w-full"
                        disabled={!proposedDate || !proposedTime}
                      >
                        Propose This Time
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CoachBookingAction;
